import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification,
  signInWithPopup,
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { db, auth, googleProvider } from "../firebaseConfig";

export const UserContext = React.createContext();

export const UserStorage = ({ children }) => {
  const [user, setUser] = React.useState(null);
  const [userData, setUserData] = useState(null);
  const [userResearch, setUserResearch] = useState(null);
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [sessionTime, setSessionTime] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setSessionTime((prevTime) => prevTime + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    onAuthStateChanged(auth, (currentUser) => {
      setLoading(true);
      if (currentUser && !userData) {
        setUser(currentUser);
        getDocument("users", currentUser.uid).then(function (result) {
          setUserData(result);
        });
        setLoading(false);
      } else if (currentUser && !userResearch) {
        getDocument("wordMood", currentUser.uid).then(function (result) {
          setUserResearch(result);
        });
        setLoading(false);
      } else {
        setLoading(false);
      }
    });
  }, [userData, userResearch]);

  const userRegister = async (displayName, email, password) => {
    try {
      setError(null);
      setLoading(true);

      // Cria o usuário no Auth
      const res = await createUserWithEmailAndPassword(auth, email, password);
      const u = res.user;

      // Atualiza o nome de exibição
      await updateProfile(u, {
        displayName: displayName,
      });

      // Envia verificação de email (apenas fora do emulador)
      if (window.location.hostname !== "localhost") {
        await sendEmailVerification(u)
          .then(() => console.log("Email verification sent!"))
          .catch((err) =>
            console.warn("Erro ao enviar verificação:", err.message),
          );
      }

      // Cria o documento no Firestore
      const userData = {
        id: u.uid,
        displayName,
        email,
        userType: {
          category: "user",
          subType: null,
          status: "active",
          premiumReport: false,
        },
        profilePic: false,
      };

      await setDoc(doc(db, "users", u.uid), userData);

      // setUser(u);
      // setUserData(userData);
      navigate("/admin");
    } catch (err) {
      console.error("Erro ao registrar usuário:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const userLogin = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      const u = await signInWithEmailAndPassword(auth, email, password);
      setUser(u.user);
      setUserData(getDocument("users", u.user.uid));
      navigate("/");
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setError(null);
      setLoading(true);
      const googleUser = await signInWithPopup(auth, googleProvider);
      await updateProfile(auth.currentUser, {
        displayName: googleUser.user.displayName,
      });
      let userLogging = "";
      await getDocument("users", auth.currentUser.uid).then(function (result) {
        userLogging = result;
      });
      await setDoc(doc(db, "users", auth.currentUser.uid), {
        id: auth.currentUser.uid,
        displayName: googleUser.user.displayName,
        email: googleUser.user.email,
        category: userLogging ? userLogging.category : "patient",
        research: userLogging ? userLogging.research : false,
        member: userLogging ? userLogging.member : false,
        staff: userLogging ? userLogging.staff : false,
        profilePic: googleUser.user.photoURL ? googleUser.user.photoURL : false,
      });
      setUser(googleUser.user);
      await getDocument("users", auth.currentUser.uid).then(function (result) {
        setUserData(result);
      });
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getDocument = async (collection, uid) => {
    const docRef = doc(db, collection, uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      console.log(collection + " loaded");
      return docSnap.data();
    } else {
      // doc.data() will be undefined in this case
      console.log("No such document!");
      return false;
    }
  };

  const userLogout = () => {
    signOut(auth);
    setUserData(null);
    setUser(null);
    navigate("/");
  };

  const passwordReset = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      navigate("/");
      alert("Password reset link sent!");
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };

  return (
    <UserContext.Provider
      value={{
        userRegister,
        userLogin,
        user,
        setUser,
        userData,
        setUserData,
        userResearch,
        setUserResearch,
        userList,
        userLogout,
        getDocument,
        loading,
        error,
        passwordReset,
        signInWithGoogle,
        sessionTime,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
